/**
 * Minimal Consumer analytics consent (KAN-674).
 *
 * Necessary auth/session/checkout cookies are independent of this choice.
 * Persist without coupling to login. Consent cannot override an environment
 * kill switch (GTM / Event traffic flags stay fail-closed).
 */

import {
  ANALYTICS_EVENT_TRAFFIC_FLAG,
  ANALYTICS_GTM_FLAG,
  ANALYTICS_SEGMENT_FLAG,
  isFeatureEnabled,
  loadConfig,
  type AppConfig,
} from "@project-x/config";

export const ANALYTICS_CONSENT_STORAGE_KEY = "gruv_analytics_consent";
export const ANALYTICS_CONSENT_COOKIE_NAME = "gruv_analytics_consent";
export const ANALYTICS_CONSENT_EVENT = "gruv-analytics-consent";
export const ANALYTICS_CONSENT_TTL_SECONDS = 180 * 24 * 60 * 60;

export type AnalyticsConsentChoice = "accepted" | "necessary";

const CONSENT_PATH = "/api/analytics/consent";

function readCookie(name: string): string | undefined {
  if (typeof document === "undefined") return undefined;
  const parts = document.cookie.split(";");
  for (const part of parts) {
    const separator = part.indexOf("=");
    if (separator <= 0) continue;
    if (part.slice(0, separator).trim() !== name) continue;
    return decodeURIComponent(part.slice(separator + 1).trim());
  }
  return undefined;
}

function writeConsentCookie(choice: AnalyticsConsentChoice): void {
  if (typeof document === "undefined") return;
  const secure = typeof location !== "undefined" && location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${ANALYTICS_CONSENT_COOKIE_NAME}=${encodeURIComponent(choice)}; Path=/; Max-Age=${ANALYTICS_CONSENT_TTL_SECONDS}; SameSite=Lax${secure}`;
}

function parseConsent(raw: string | null | undefined): AnalyticsConsentChoice | null {
  if (raw === "accepted" || raw === "necessary") return raw;
  return null;
}

/** Stored choice, or null when the user has not decided. */
export function readAnalyticsConsent(): AnalyticsConsentChoice | null {
  if (typeof window === "undefined") return null;
  try {
    const stored = parseConsent(window.localStorage.getItem(ANALYTICS_CONSENT_STORAGE_KEY));
    if (stored) return stored;
  } catch {
    // Private mode / blocked storage.
  }
  return parseConsent(readCookie(ANALYTICS_CONSENT_COOKIE_NAME));
}

export function hasAcceptedAnalyticsConsent(): boolean {
  return readAnalyticsConsent() === "accepted";
}

/** True when GTM, Segment, or first-party Event traffic can collect. */
export function isAnalyticsSurfaceEnabled(config: AppConfig = loadConfig()): boolean {
  return (
    isFeatureEnabled(config, ANALYTICS_EVENT_TRAFFIC_FLAG) ||
    isFeatureEnabled(config, ANALYTICS_GTM_FLAG) ||
    isFeatureEnabled(config, ANALYTICS_SEGMENT_FLAG)
  );
}

function notifyConsent(choice: AnalyticsConsentChoice): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(ANALYTICS_CONSENT_EVENT, { detail: choice }));
}

function postConsent(choice: AnalyticsConsentChoice): void {
  if (typeof window === "undefined") return;
  try {
    void fetch(CONSENT_PATH, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ choice }),
      credentials: "same-origin",
      keepalive: true,
    }).catch(() => undefined);
  } catch {
    // Consent persistence to the gateway is best-effort.
  }
}

/** Persists the choice locally and notifies the gateway. Never throws. */
export function persistAnalyticsConsent(choice: AnalyticsConsentChoice): void {
  try {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(ANALYTICS_CONSENT_STORAGE_KEY, choice);
    }
  } catch {
    // Continue with the cookie even if localStorage is blocked.
  }
  writeConsentCookie(choice);
  postConsent(choice);
  notifyConsent(choice);
}
